import { SemanticICONS } from 'semantic-ui-react';
import { tl } from 'common/services/i18nService';
import { hasAccessToEventLogsForUser } from 'common/services/permissions/commonPermissionService';
import { OptionsMenuActionsName } from '../store/optionsMenu/types';
import store from '../store';
import { RootState } from '../store/rootReducer';
import { getBelongedCommittees } from './committeeService';
import { workflowTabs } from '../features/workflowOverlay/reducer';
import {
    hasCurrentUserPlannerPrivilege, isScoreOrGradeReadOnlyOnTestOrCommittee,
} from './permissions/PermissionService';
import {
    isQuestionAnswerKeyEnabled,
    isQuestionAssignmentActionEnabled,
    isQuestionOverrideWeightActionEnabled,
} from '../features/workflowSettings/services/workflowSettingsService';
import { WorkflowPrimaryActions } from '../store/context/types';

export enum OptionsMenuSubtitles {
    WORKFLOW = 'workflow',
    DOWNLOADS = 'downloads',
    SETTINGS = 'settings',
    UPLOADS = 'uploads',
    SHORTCUTS = 'shortcuts',
    EXTERNAL_SYSTEM = 'external_system',
    MODERATION = 'moderation',
}

export enum ActionsFromBackendPermission {
    END_TEST = 'END_TEST',
    SHARE_OWN_GRADES = 'SHARE_OWN_GRADES',
    CALCULATE_GRADES = 'CALCULATE_GRADES',
    CONFIRM_FINAL_GRADES = 'CONFIRM_FINAL_GRADES',
    ALIGN_GRADES = 'ALIGN_GRADES',
    DOWNLOAD_PROTOCOL = 'DOWNLOAD_PROTOCOL',
    BYPASS_PRIVATE_GRADING = 'BYPASS_PRIVATE_GRADING',
    CONFIRM_ONBEHALF_GRADERS = 'CONFIRM_ONBEHALF_GRADERS',
    REOPEN_GRADING = 'REOPEN_GRADING',
    TRANSFER_GRADES = 'TRANSFER_GRADES',
    TRANSFER_GRADES_COMMITTEE = 'TRANSFER_GRADES_COMMITTEE',
    DEANONYMIZE_CANDIDATES = 'DEANONYMIZE_CANDIDATES',
    DOWNLOAD_ALL_SUBMISSIONS_PDF = 'DOWNLOAD_ALL_SUBMISSIONS_PDF',
    DOWNLOAD_COMMITTEE_SUBMISSIONS_PDF = 'DOWNLOAD_COMMITTEE_SUBMISSIONS_PDF',
    GRADES_AS_EXCEL = 'GRADES_AS_EXCEL',
    CANDIDATE_DISTRACTOR_REPORT_CSV = 'CANDIDATE_DISTRACTOR_REPORT_CSV',
    UPLOAD_GRADES_EXCEL = 'UPLOAD_GRADES_EXCEL',
    MARKS_GRADES_CSV = 'MARKS_GRADES_CSV',
    DOWNLOAD_ALL_DATA_FOR_MULTIPLE_ATTEMPTS = 'DOWNLOAD_ALL_DATA_FOR_MULTIPLE_ATTEMPTS_AS_CSV',
    DOWNLOAD_FINAL_DATA_FOR_MULTIPLE_ATTEMPTS = 'DOWNLOAD_FINAL_DATA_FOR_MULTIPLE_ATTEMPTS_AS_CSV',
    EXPORT_JAGGED_ANALYSIS_REPORT = 'EXPORT_JAGGED_ANALYSIS_REPORT',
    TOTAL_MARKS_GRADES_CSV = 'TOTAL_MARKS_GRADES_CSV',
    MARKS_AS_EXCEL = 'MARKS_AS_EXCEL',
    QUESTION_DISTRACTOR_ANALYSIS_CSV = 'QUESTION_DISTRACTOR_ANALYSIS_CSV',
    UNAPPROVE_OWN_GRADES = 'UNAPPROVE_OWN_GRADES',
    UNCONFIRM_FINAL_GRADES = 'UNCONFIRM_FINAL_GRADES',
    MARKS_CRITERIA_AS_EXCEL = 'MARKS_CRITERIA_AS_EXCEL',
    MARKS_IMPORT_AS_EXCEL = 'MARKS_IMPORT_AS_EXCEL',
}

export interface MenuItemsGroup {
    groupHeader: string;
    actions: MenuItemAction[];
}

export interface MenuItemAction {
    actionName: string;
    hasConfirmModal?: boolean;
    confirmModalInstructions?: string[];
    confirmModalHasCommentTextarea?: boolean;
    confirmModalShowCloseIcon?: boolean;
    confirmModalConfirmButtonLabel?: string;
    notSubmittedCandidatesWarning?: boolean;
    isNotVisible?: boolean;
    icon?: SemanticICONS;
    nested?: MenuItemAction[];
    visibleWithFile?: boolean;
    syncConfirmation?: boolean;
    workflowAction?: WorkflowPrimaryActions;
    openNewTabFromIA?: boolean;
}

interface Action {
    allowed: boolean;
    enabled: boolean;
    marketplacePropertyDependency: string;
    name: string;
}

export const getWorkflowTabIndexByActionsMenu = (actionName: OptionsMenuActionsName): number => {
    switch (actionName) {
    case OptionsMenuActionsName.OPEN_WORKFLOW_IN_SHARE:
        return workflowTabs.SHARE.index;
    case OptionsMenuActionsName.OPEN_WORKFLOW_IN_ALIGN:
        return workflowTabs.ALIGN.index;
    case OptionsMenuActionsName.OPEN_WORKFLOW_IN_CONFIRM:
        return workflowTabs.CONFIRM.index;
    case OptionsMenuActionsName.OPEN_WORKFLOW_SETTINGS:
        return workflowTabs.SETUP.index;
    case OptionsMenuActionsName.MANAGE_COMMITTEE_MEMBERS:
        return workflowTabs.MEMBERS.index;
    default:
        return undefined;
    }
};

export const isCurrentActionEnabledFromBackend = (actions: Action[], actionName: ActionsFromBackendPermission): boolean => {
    const action = actions.find((act: Action) => {
        return act.name === actionName.toLowerCase();
    });

    return action && action.allowed;
};

export const currentActionForPlannerIsVisible = (actionName: ActionsFromBackendPermission) => {
    const appState = store.getState() as RootState;
    const { context } = appState;
    const { workflowInfo } = context;
    return workflowInfo
        && workflowInfo.plannerSecondaryActions
        && isCurrentActionEnabledFromBackend(workflowInfo.plannerSecondaryActions, actionName);
};

export const getOptionsMenuItems = (): MenuItemsGroup[] => {
    const appState = store.getState() as RootState;
    const { context, assessment } = appState;
    const userBelongsCommittees = getBelongedCommittees(assessment.committees);
    const { workflowInfo, selectedCommitteeId } = context;
    const userHasPlannerRole = hasCurrentUserPlannerPrivilege();
    const explanationsExportModalInstructions = [
        tl('grading.optionsMenu.action.instructions.overwiteInput'),
        tl('grading.optionsMenu.action.instructions.richTextNotAllowed'),
        tl('grading.optionsMenu.action.instructions.provideExplanation'),
    ];
    const currentActionForGraderIsVisible = (actionName: ActionsFromBackendPermission) => {
        return workflowInfo
            && workflowInfo.evaluatorSecondaryActions
            && isCurrentActionEnabledFromBackend(workflowInfo.evaluatorSecondaryActions, actionName);
    };
    const currentActionForGraderOrPlannerIsVisible = (actionName: ActionsFromBackendPermission) => {
        return currentActionForPlannerIsVisible(actionName) || currentActionForGraderIsVisible(actionName);
    };

    const isDownloadAndPrintAllSubmissionsVisible = () => {
        return (
            context.selectedCommitteeId <= 0
            && currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.DOWNLOAD_ALL_SUBMISSIONS_PDF)
        );
    };

    const isDownloadAndPrintCommitteeSubmissionsVisible = () => {
        return (
            context.selectedCommitteeId > 0
            && currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.DOWNLOAD_COMMITTEE_SUBMISSIONS_PDF)
        );
    };

    const isOpenThresholdEditorActionNotVisible = () => {
        return !assessment?.useGradingThresholds
            || assessment?.isAutomatedTestConstructionEnabled
            || assessment?.isMarksOnlyExam
            || (assessment.isAssessmentPath && assessment.isDecoupleGradingOnParentFromChildrenEnabled);
    };

    return [
        {
            groupHeader: OptionsMenuSubtitles.WORKFLOW,
            actions: [
                {
                    actionName: OptionsMenuActionsName.CLOSE_TEST_TO_START_GRADING,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForPlannerIsVisible(ActionsFromBackendPermission.END_TEST),
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_WORKFLOW_IN_SHARE,
                    workflowAction: WorkflowPrimaryActions.SHARE_OWN_GRADES,
                    hasConfirmModal: true,
                    notSubmittedCandidatesWarning: true,
                    isNotVisible: !currentActionForGraderIsVisible(ActionsFromBackendPermission.SHARE_OWN_GRADES),
                },
                {
                    actionName: OptionsMenuActionsName.BYPASS_PRIVATE_GRADING,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForPlannerIsVisible(ActionsFromBackendPermission.BYPASS_PRIVATE_GRADING)
                        || selectedCommitteeId < 0,
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_WORKFLOW_IN_ALIGN,
                    isNotVisible: !currentActionForGraderIsVisible(ActionsFromBackendPermission.ALIGN_GRADES),
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_WORKFLOW_IN_CONFIRM,
                    workflowAction: WorkflowPrimaryActions.CONFIRM_FINAL_GRADES,
                    hasConfirmModal: true,
                    notSubmittedCandidatesWarning: true,
                    isNotVisible: !currentActionForGraderIsVisible(ActionsFromBackendPermission.CONFIRM_FINAL_GRADES),
                },
                {
                    actionName: OptionsMenuActionsName.CONFIRM_GRADES_ON_BEHALF_OF_GRADERS,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForPlannerIsVisible(ActionsFromBackendPermission.CONFIRM_ONBEHALF_GRADERS)
                        || selectedCommitteeId < 0,
                },
                {
                    actionName: OptionsMenuActionsName.UNCONFIRM_FINAL_GRADES,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForGraderIsVisible(ActionsFromBackendPermission.UNCONFIRM_FINAL_GRADES),
                },
                {
                    actionName: OptionsMenuActionsName.UNAPPROVE_OWN_GRADES,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForGraderIsVisible(ActionsFromBackendPermission.UNAPPROVE_OWN_GRADES),
                },
                {
                    actionName: OptionsMenuActionsName.REOPEN_GRADING,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForPlannerIsVisible(ActionsFromBackendPermission.REOPEN_GRADING)
                    || (!assessment.isAssessmentPath && !assessment.isMultipleAttemptsParent && (assessment.compositeInfo
                    && assessment.compositeInfo.children
                    && assessment.compositeInfo.children.length > 0)),
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_WORKFLOW_SETTINGS,
                    isNotVisible: true,
                    // isNotVisible: !userHasPlannerRole,
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_COMMITTEE_SELECTOR,
                    isNotVisible: !(userBelongsCommittees.length > 1),
                },
            ],
        },
        {
            groupHeader: OptionsMenuSubtitles.SETTINGS,
            actions: [
                {
                    actionName: OptionsMenuActionsName.OPEN_QUESTION_WEIGHTS_EDITOR,
                    isNotVisible: !isQuestionOverrideWeightActionEnabled(),
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_GRADES_THRESHOLD_EDITOR,
                    // isNotVisible: !currentActionForGraderIsVisible(ActionsFromBackendPermission.CALCULATE_GRADES),
                    isNotVisible: isOpenThresholdEditorActionNotVisible(),
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_GRADES_THRESHOLD_EDITOR_UPDATE,
                    isNotVisible: isOpenThresholdEditorActionNotVisible(),
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_QUESTION_ASSIGNMENTS_EDITOR,
                    isNotVisible: !isQuestionAssignmentActionEnabled(),
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_ANSWER_KEY_CORRECTION_EDITOR,
                    isNotVisible: !isQuestionAnswerKeyEnabled(),
                },
                {
                    actionName: OptionsMenuActionsName.SHOW_CANDIDATES_REAL_NAMES,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForPlannerIsVisible(ActionsFromBackendPermission.DEANONYMIZE_CANDIDATES),
                },
            ],
        },
        {
            groupHeader: OptionsMenuSubtitles.DOWNLOADS,
            actions: [
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_MARK_SCHEME_SUBMENU,
                    isNotVisible: false,
                    nested: [
                        {
                            actionName: OptionsMenuActionsName.UPLOAD_MARK_SCHEME,
                            isNotVisible: false,
                            visibleWithFile: false,
                            icon: 'upload',
                        },
                        {
                            actionName: OptionsMenuActionsName.DOWNLOAD_MARK_SCHEME,
                            isNotVisible: false,
                            visibleWithFile: true,
                            icon: 'download',
                        },
                        {
                            actionName: OptionsMenuActionsName.REPLACE_MARK_SCHEME,
                            isNotVisible: false,
                            visibleWithFile: true,
                            icon: 'folder open outline',
                        },
                        {
                            actionName: OptionsMenuActionsName.REMOVE_MARK_SCHEME,
                            isNotVisible: false,
                            visibleWithFile: true,
                            icon: 'trash alternate',
                        },
                    ],
                },
                {
                    actionName: OptionsMenuActionsName.OPEN_GRADING_PROTOCOL,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.DOWNLOAD_PROTOCOL),
                    openNewTabFromIA: true,
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_AND_PRINT_ALL_SUBMISSIONS_PDF,
                    isNotVisible: !isDownloadAndPrintAllSubmissionsVisible(),
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_AND_PRINT_COMMITTEE_SUBMISSIONS_PDF,
                    isNotVisible: !isDownloadAndPrintCommitteeSubmissionsVisible(),
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_MARKS_AS_EXCEL,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.MARKS_AS_EXCEL),
                    confirmModalInstructions: explanationsExportModalInstructions,
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_GRADES_AS_EXCEL,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.GRADES_AS_EXCEL),
                    confirmModalInstructions: explanationsExportModalInstructions,
                },
                {
                    actionName: OptionsMenuActionsName.EXPORT_JAGGED_ANALYSIS_RESULT_TO_MARK_MANAGER,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.EXPORT_JAGGED_ANALYSIS_REPORT),
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_EXCEL_MARKING_SHEET,
                    isNotVisible: true, // hidden for now due to we do not have upload
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_MARKS_AND_CRITERIA,
                    isNotVisible: !currentActionForPlannerIsVisible(ActionsFromBackendPermission.MARKS_CRITERIA_AS_EXCEL),
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_MARKS_AND_GRADES_AS_CSV,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.MARKS_GRADES_CSV),
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_TOTAL_MARKS_AND_GRADES_AS_CSV,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.TOTAL_MARKS_GRADES_CSV),
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_ANALYSIS_AS_CSV,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.QUESTION_DISTRACTOR_ANALYSIS_CSV),
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_CANDIDATE_REPORT_AS_CSV,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.CANDIDATE_DISTRACTOR_REPORT_CSV),
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_MULTIPLE_ATTEMPTS_ALL_DATA_AS_CSV,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.DOWNLOAD_ALL_DATA_FOR_MULTIPLE_ATTEMPTS),
                },
                {
                    actionName: OptionsMenuActionsName.DOWNLOAD_MULTIPLE_ATTEMPTS_FINAL_DATA_AS_CSV,
                    isNotVisible: !currentActionForGraderOrPlannerIsVisible(ActionsFromBackendPermission.DOWNLOAD_FINAL_DATA_FOR_MULTIPLE_ATTEMPTS),
                },
            ],
        },
        {
            groupHeader: OptionsMenuSubtitles.SHORTCUTS,
            actions: [
                {
                    actionName: OptionsMenuActionsName.QUESTION_SET,
                    isNotVisible: false,
                    nested: [
                        {
                            actionName: OptionsMenuActionsName.VIEW_QUESTION_SET_AS_STUDENT,
                            icon: 'external alternate',
                            openNewTabFromIA: true,
                        },
                        {
                            actionName: OptionsMenuActionsName.VIEW_QUESTION_SET_WITH_CORRECT_ANSWERS,
                            icon: 'external alternate',
                            openNewTabFromIA: true,
                        },
                        {
                            actionName: OptionsMenuActionsName.PRINT_AND_DOWNLOAD_QUESTION_SET,
                            icon: 'file pdf outline',
                        },
                    ],
                },
                {
                    actionName: OptionsMenuActionsName.DELIVER,
                    isNotVisible: !userHasPlannerRole,
                    nested: [
                        {
                            actionName: OptionsMenuActionsName.OPEN_TEST_IN_DELIVER,
                            openNewTabFromIA: true,
                        },
                        {
                            actionName: OptionsMenuActionsName.INVITE_GRADER,
                            openNewTabFromIA: true,
                        },
                        {
                            actionName: OptionsMenuActionsName.MANAGE_CONTRIBUTORS,
                            openNewTabFromIA: true,
                        },
                        {
                            actionName: OptionsMenuActionsName.MANAGE_COMMITTEES,
                            openNewTabFromIA: true,
                        },
                        // Here is missing 2 actions
                    ],
                },
                {
                    actionName: OptionsMenuActionsName.MONITOR,
                    nested: [
                        {
                            actionName: OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR,
                            openNewTabFromIA: true,
                        },
                        {
                            actionName: OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR_IN_PROGRESS,
                            openNewTabFromIA: true,
                        },
                        {
                            actionName: OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR_SUBMITTED,
                            openNewTabFromIA: true,
                        },
                        {
                            actionName: OptionsMenuActionsName.VIEW_CANDIDATE_IN_MONITOR_WITHDRAWN,
                            openNewTabFromIA: true,
                        },
                    ],
                },
                {
                    actionName: OptionsMenuActionsName.MANAGE_COMMITTEE_MEMBERS,
                    isNotVisible: true,
                    // isNotVisible: !userHasPlannerRole,
                },
                {
                    actionName: OptionsMenuActionsName.EVENT_LOG,
                    openNewTabFromIA: true,
                    isNotVisible: !hasAccessToEventLogsForUser(),
                },
            ],
        },
        {
            groupHeader: OptionsMenuSubtitles.UPLOADS,
            actions: [
                {
                    actionName: OptionsMenuActionsName.UPLOAD_GRADES_FROM_EXCEL,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForGraderIsVisible(ActionsFromBackendPermission.UPLOAD_GRADES_EXCEL)
                        || isScoreOrGradeReadOnlyOnTestOrCommittee(),
                },
                {
                    actionName: OptionsMenuActionsName.MARKS_IMPORT_AS_EXCEL,
                    hasConfirmModal: true,
                    isNotVisible: !currentActionForGraderIsVisible(ActionsFromBackendPermission.MARKS_IMPORT_AS_EXCEL)
                        || isScoreOrGradeReadOnlyOnTestOrCommittee(),
                },
            ],
        },
        {
            groupHeader: OptionsMenuSubtitles.MODERATION,
            actions: [
                {
                    actionName: OptionsMenuActionsName.SET_MODERATION_STATUS_APPROVE,
                    hasConfirmModal: true,
                    isNotVisible: true,
                    confirmModalHasCommentTextarea: true,
                    confirmModalShowCloseIcon: true,
                    confirmModalConfirmButtonLabel: 'grading.optionsMenu.modal.button.iApprove',
                },
                {
                    actionName: OptionsMenuActionsName.SET_MODERATION_STATUS_DO_NOT_APPROVE,
                    hasConfirmModal: true,
                    isNotVisible: true,
                    confirmModalHasCommentTextarea: true,
                    confirmModalShowCloseIcon: true,
                    confirmModalConfirmButtonLabel: 'grading.optionsMenu.modal.button.iDoNotApprove',
                },
            ],
        },
    ];
};
